import * as i0 from '@angular/core';
import { Directive, HostBinding, HostListener, forwardRef, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR, CheckboxControlValueAccessor } from '@angular/forms';
import { CommonModule } from '@angular/common';

/** Base class for all Orchid `ControlValueAccessor` implementations that use `value` in a `change` event, as opposed to `checked`. */
class OrchidBaseValueAccessor {
    constructor() {
        this.disabled = false;
        this.value = '';
    }
    _onChange(_value) { }
    _onTouched(_value) { }
    registerOnChange(fn) {
        this._onChange = fn;
    }
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    writeValue(value) {
        this.value = value;
    }
    static { this.ɵfac = function OrchidBaseValueAccessor_Factory(t) { return new (t || OrchidBaseValueAccessor)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: OrchidBaseValueAccessor, hostVars: 2, hostBindings: function OrchidBaseValueAccessor_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("change", function OrchidBaseValueAccessor_change_HostBindingHandler($event) { return ctx._onChange($event.target.value); })("blur", function OrchidBaseValueAccessor_blur_HostBindingHandler() { return ctx._onTouched(); });
        } if (rf & 2) {
            i0.ɵɵhostProperty("disabled", ctx.disabled)("value", ctx.value);
        } } }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrchidBaseValueAccessor, [{
        type: Directive
    }], null, { disabled: [{
            type: HostBinding,
            args: ['disabled']
        }], value: [{
            type: HostBinding,
            args: ['value']
        }], _onChange: [{
            type: HostListener,
            args: ['change', ['$event.target.value']]
        }], _onTouched: [{
            type: HostListener,
            args: ['blur']
        }] }); })();

/**
 * @description
 * A `ControlValueAccessor` for writing a value and listening to changes on an `<ods-autosuggest>` element.
 *
 * @usageNotes
 *
 * ### Using an Orchid autosuggest with a reactive form.
 *
 * The following example shows how to use an autosuggest with a reactive form.
 *
 * ```ts
 * const autosuggestControl = new FormControl();
 * const options = ['Option A', 'Option B', 'Option C'];
 * ```
 *
 * ```
 * <ods-autosuggest [formControl]="autosuggestControl" [options]="options"></ods-autosuggest>
 * ```
 *
 * @ngModule ReactiveFormsModule
 * @ngModule FormsModule
 * @publicApi
 */
class OrchidAutosuggestValueAccessorDirective extends OrchidBaseValueAccessor {
    static { this.ɵfac = /*@__PURE__*/ function () { let ɵOrchidAutosuggestValueAccessorDirective_BaseFactory; return function OrchidAutosuggestValueAccessorDirective_Factory(t) { return (ɵOrchidAutosuggestValueAccessorDirective_BaseFactory || (ɵOrchidAutosuggestValueAccessorDirective_BaseFactory = i0.ɵɵgetInheritedFactory(OrchidAutosuggestValueAccessorDirective)))(t || OrchidAutosuggestValueAccessorDirective); }; }(); }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: OrchidAutosuggestValueAccessorDirective, selectors: [["ods-autosuggest", "formControl", ""], ["ods-autosuggest", "formControlName", ""], ["ods-autosuggest", "ngModel", ""], ["ods-autosuggest", "odsFormControl", ""]], hostVars: 1, hostBindings: function OrchidAutosuggestValueAccessorDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("ngModelChange", function OrchidAutosuggestValueAccessorDirective_ngModelChange_HostBindingHandler($event) { return ctx.value = $event; });
        } if (rf & 2) {
            i0.ɵɵhostProperty("ngModel", ctx.value);
        } }, features: [i0.ɵɵProvidersFeature([
                {
                    multi: true,
                    provide: NG_VALUE_ACCESSOR,
                    useExisting: forwardRef(() => OrchidAutosuggestValueAccessorDirective)
                }
            ]), i0.ɵɵInheritDefinitionFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrchidAutosuggestValueAccessorDirective, [{
        type: Directive,
        args: [{
                host: {
                    '(ngModelChange)': 'value = $event',
                    '[ngModel]': 'value'
                },
                providers: [
                    {
                        multi: true,
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef(() => OrchidAutosuggestValueAccessorDirective)
                    }
                ],
                selector: 'ods-autosuggest[formControl],ods-autosuggest[formControlName],ods-autosuggest[ngModel],ods-autosuggest[odsFormControl]'
            }]
    }], null, null); })();

/**
 * @description
 * A `ControlValueAccessor` for writing a value and listening to changes on an `<ods-card>` element with `behavior="selectable"`.
 *
 * @usageNotes
 *
 * ### Using an Orchid selectable card with a reactive form.
 *
 * The following example shows how to use a selectable card with a reactive form.
 *
 * ```ts
 * const selectableCardControl = new FormControl();
 * ```
 *
 * ```
 * <ods-card [formControl]="selectableCardControl" behavior="selectable" value="card">
 *  <p>Content goes here</p>
 * </ods-card>
 * ```
 *
 * @ngModule ReactiveFormsModule
 * @ngModule FormsModule
 * @publicApi
 */
class OrchidCardValueAccessorDirective extends CheckboxControlValueAccessor {
    static { this.ɵfac = /*@__PURE__*/ function () { let ɵOrchidCardValueAccessorDirective_BaseFactory; return function OrchidCardValueAccessorDirective_Factory(t) { return (ɵOrchidCardValueAccessorDirective_BaseFactory || (ɵOrchidCardValueAccessorDirective_BaseFactory = i0.ɵɵgetInheritedFactory(OrchidCardValueAccessorDirective)))(t || OrchidCardValueAccessorDirective); }; }(); }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: OrchidCardValueAccessorDirective, selectors: [["ods-card", "formControl", "", "behavior", "selectable"], ["ods-card", "formControlName", "", "behavior", "selectable"], ["ods-card", "ngModel", "", "behavior", "selectable"], ["ods-card", "odsFormControl", "", "behavior", "selectable"]], hostBindings: function OrchidCardValueAccessorDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("blur", function OrchidCardValueAccessorDirective_blur_HostBindingHandler() { return ctx.onTouched(); })("change", function OrchidCardValueAccessorDirective_change_HostBindingHandler($event) { return ctx.onChange($event.target.checked); });
        } }, features: [i0.ɵɵProvidersFeature([
                {
                    multi: true,
                    provide: NG_VALUE_ACCESSOR,
                    useExisting: forwardRef(() => OrchidCardValueAccessorDirective)
                }
            ]), i0.ɵɵInheritDefinitionFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrchidCardValueAccessorDirective, [{
        type: Directive,
        args: [{
                host: { '(blur)': 'onTouched()', '(change)': 'onChange($event.target.checked)' },
                providers: [
                    {
                        multi: true,
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef(() => OrchidCardValueAccessorDirective)
                    }
                ],
                selector: 'ods-card[formControl][behavior="selectable"],ods-card[formControlName][behavior="selectable"],ods-card[ngModel][behavior="selectable"],ods-card[odsFormControl][behavior="selectable"]'
            }]
    }], null, null); })();

/**
 * @description
 * A `ControlValueAccessor` for writing a value and listening to changes on an `<ods-checkbox>` element.
 *
 * @usageNotes
 *
 * ### Using an Orchid checkbox with a reactive form.
 *
 * The following example shows how to use a checkbox with a reactive form.
 *
 * ```ts
 * const checkboxControl = new FormControl();
 * ```
 *
 * ```
 * <ods-checkbox [formControl]="checkboxControl" label="Checkbox" value="checkbox"></ods-checkbox>
 * ```
 *
 * @ngModule ReactiveFormsModule
 * @ngModule FormsModule
 * @publicApi
 */
class OrchidCheckboxValueAccessorDirective extends CheckboxControlValueAccessor {
    static { this.ɵfac = /*@__PURE__*/ function () { let ɵOrchidCheckboxValueAccessorDirective_BaseFactory; return function OrchidCheckboxValueAccessorDirective_Factory(t) { return (ɵOrchidCheckboxValueAccessorDirective_BaseFactory || (ɵOrchidCheckboxValueAccessorDirective_BaseFactory = i0.ɵɵgetInheritedFactory(OrchidCheckboxValueAccessorDirective)))(t || OrchidCheckboxValueAccessorDirective); }; }(); }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: OrchidCheckboxValueAccessorDirective, selectors: [["ods-checkbox", "formControl", ""], ["ods-checkbox", "formControlName", ""], ["ods-checkbox", "ngModel", ""], ["ods-checkbox", "odsFormControl", ""]], hostBindings: function OrchidCheckboxValueAccessorDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("blur", function OrchidCheckboxValueAccessorDirective_blur_HostBindingHandler() { return ctx.onTouched(); })("change", function OrchidCheckboxValueAccessorDirective_change_HostBindingHandler($event) { return ctx.onChange($event.target.checked); });
        } }, features: [i0.ɵɵProvidersFeature([
                {
                    multi: true,
                    provide: NG_VALUE_ACCESSOR,
                    useExisting: forwardRef(() => OrchidCheckboxValueAccessorDirective)
                }
            ]), i0.ɵɵInheritDefinitionFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrchidCheckboxValueAccessorDirective, [{
        type: Directive,
        args: [{
                host: { '(blur)': 'onTouched()', '(change)': 'onChange($event.target.checked)' },
                providers: [
                    {
                        multi: true,
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef(() => OrchidCheckboxValueAccessorDirective)
                    }
                ],
                selector: 'ods-checkbox[formControl],ods-checkbox[formControlName],ods-checkbox[ngModel],ods-checkbox[odsFormControl]'
            }]
    }], null, null); })();

/**
 * @description
 * A `ControlValueAccessor` for writing a value and listening to changes on an `<ods-date-picker>` element.
 *
 * @usageNotes
 *
 * ### Using an Orchid date picker with a reactive form.
 *
 * The following example shows how to use a date picker with a reactive form.
 *
 * ```ts
 * const datePickerControl = new FormControl();
 * ```
 *
 * ```
 * <ods-date-picker [formControl]="datePickerControl"></date-picker
 * ```
 *
 * @ngModule ReactiveFormsModule
 * @ngModule FormsModule
 * @publicApi
 */
class OrchidDatePickerValueAccessorDirective extends OrchidBaseValueAccessor {
    static { this.ɵfac = /*@__PURE__*/ function () { let ɵOrchidDatePickerValueAccessorDirective_BaseFactory; return function OrchidDatePickerValueAccessorDirective_Factory(t) { return (ɵOrchidDatePickerValueAccessorDirective_BaseFactory || (ɵOrchidDatePickerValueAccessorDirective_BaseFactory = i0.ɵɵgetInheritedFactory(OrchidDatePickerValueAccessorDirective)))(t || OrchidDatePickerValueAccessorDirective); }; }(); }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: OrchidDatePickerValueAccessorDirective, selectors: [["ods-date-picker", "formControl", ""], ["ods-date-picker", "formControlName", ""], ["ods-date-picker", "ngModel", ""], ["ods-date-picker", "odsFormControl", ""]], hostVars: 1, hostBindings: function OrchidDatePickerValueAccessorDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("ngModelChange", function OrchidDatePickerValueAccessorDirective_ngModelChange_HostBindingHandler($event) { return ctx.value = $event; });
        } if (rf & 2) {
            i0.ɵɵhostProperty("ngModel", ctx.value);
        } }, features: [i0.ɵɵProvidersFeature([
                {
                    multi: true,
                    provide: NG_VALUE_ACCESSOR,
                    useExisting: forwardRef(() => OrchidDatePickerValueAccessorDirective)
                }
            ]), i0.ɵɵInheritDefinitionFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrchidDatePickerValueAccessorDirective, [{
        type: Directive,
        args: [{
                host: {
                    '(ngModelChange)': 'value = $event',
                    '[ngModel]': 'value'
                },
                providers: [
                    {
                        multi: true,
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef(() => OrchidDatePickerValueAccessorDirective)
                    }
                ],
                selector: 'ods-date-picker[formControl],ods-date-picker[formControlName],ods-date-picker[ngModel],ods-date-picker[odsFormControl]'
            }]
    }], null, null); })();

/**
 * @description
 * A `ControlValueAccessor` for writing a value and listening to changes on an `<ods-radio-group>` element.
 *
 * @usageNotes
 *
 * ### Using Orchid radio buttons with a reactive form.
 *
 * The following example shows how to use radio buttons with a reactive form.
 *
 * ```ts
 * const radioControl = new FormControl();
 * ```
 *
 * ```
 * <ods-radio-group [formControl]="radioControl" name="radio-group">
 *  <ods-radio label="Radio A" value="radio-a"></ods-radio>
 *  <ods-radio label="Radio B" value="radio-b"></ods-radio>
 *  <ods-radio label="Radio C" value="radio-c"></ods-radio>
 * </ods-radio-group>
 * ```
 *
 * @ngModule ReactiveFormsModule
 * @ngModule FormsModule
 * @publicApi
 */
class OrchidRadioValueAccessorDirective extends OrchidBaseValueAccessor {
    static { this.ɵfac = /*@__PURE__*/ function () { let ɵOrchidRadioValueAccessorDirective_BaseFactory; return function OrchidRadioValueAccessorDirective_Factory(t) { return (ɵOrchidRadioValueAccessorDirective_BaseFactory || (ɵOrchidRadioValueAccessorDirective_BaseFactory = i0.ɵɵgetInheritedFactory(OrchidRadioValueAccessorDirective)))(t || OrchidRadioValueAccessorDirective); }; }(); }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: OrchidRadioValueAccessorDirective, selectors: [["ods-radio-group", "formControl", ""], ["ods-radio-group", "formControlName", ""], ["ods-radio-group", "ngModel", ""], ["ods-radio-group", "odsFormControl", ""]], hostVars: 1, hostBindings: function OrchidRadioValueAccessorDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("ngModelChange", function OrchidRadioValueAccessorDirective_ngModelChange_HostBindingHandler($event) { return ctx.value = $event; });
        } if (rf & 2) {
            i0.ɵɵhostProperty("ngModel", ctx.value);
        } }, features: [i0.ɵɵProvidersFeature([
                {
                    multi: true,
                    provide: NG_VALUE_ACCESSOR,
                    useExisting: forwardRef(() => OrchidRadioValueAccessorDirective)
                }
            ]), i0.ɵɵInheritDefinitionFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrchidRadioValueAccessorDirective, [{
        type: Directive,
        args: [{
                host: {
                    '(ngModelChange)': 'value = $event',
                    '[ngModel]': 'value'
                },
                providers: [
                    {
                        multi: true,
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef(() => OrchidRadioValueAccessorDirective)
                    }
                ],
                selector: 'ods-radio-group[formControl],ods-radio-group[formControlName],ods-radio-group[ngModel],ods-radio-group[odsFormControl]'
            }]
    }], null, null); })();

/**
 * @description
 * A `ControlValueAccessor` for writing a value and listening to changes on an `<ods-segmented-control>` element.
 *
 * @usageNotes
 *
 * ### Using an Orchid segmented control with a reactive form.
 *
 * The following example shows how to use a segmented control with a reactive form.
 *
 * ```ts
 * const segmentedControl = new FormControl();
 * ```
 *
 * ```
 * <ods-segmented-control [formControl]="segmentedControl" name="segmented-control">
 *  <ods-segment label="Segment A" value="segment-a"></ods-segment>
 *  <ods-segment label="Segment B" value="segment-b"></ods-segment>
 *  <ods-segment label="Segment C" value="segment-c"></ods-segment>
 * </ods-segmented-control>
 * ```
 *
 * @ngModule ReactiveFormsModule
 * @ngModule FormsModule
 * @publicApi
 */
class OrchidSegmentedControlValueAccessorDirective extends OrchidBaseValueAccessor {
    static { this.ɵfac = /*@__PURE__*/ function () { let ɵOrchidSegmentedControlValueAccessorDirective_BaseFactory; return function OrchidSegmentedControlValueAccessorDirective_Factory(t) { return (ɵOrchidSegmentedControlValueAccessorDirective_BaseFactory || (ɵOrchidSegmentedControlValueAccessorDirective_BaseFactory = i0.ɵɵgetInheritedFactory(OrchidSegmentedControlValueAccessorDirective)))(t || OrchidSegmentedControlValueAccessorDirective); }; }(); }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: OrchidSegmentedControlValueAccessorDirective, selectors: [["ods-segmented-control", "formControl", ""], ["ods-segmented-control", "formControlName", ""], ["ods-segmented-control", "ngModel", ""], ["ods-segmented-control", "odsFormControl", ""]], hostVars: 1, hostBindings: function OrchidSegmentedControlValueAccessorDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("ngModelChange", function OrchidSegmentedControlValueAccessorDirective_ngModelChange_HostBindingHandler($event) { return ctx.value = $event; });
        } if (rf & 2) {
            i0.ɵɵhostProperty("ngModel", ctx.value);
        } }, features: [i0.ɵɵProvidersFeature([
                {
                    multi: true,
                    provide: NG_VALUE_ACCESSOR,
                    useExisting: forwardRef(() => OrchidSegmentedControlValueAccessorDirective)
                }
            ]), i0.ɵɵInheritDefinitionFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrchidSegmentedControlValueAccessorDirective, [{
        type: Directive,
        args: [{
                host: {
                    '(ngModelChange)': 'value = $event',
                    '[ngModel]': 'value'
                },
                providers: [
                    {
                        multi: true,
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef(() => OrchidSegmentedControlValueAccessorDirective)
                    }
                ],
                selector: 'ods-segmented-control[formControl],ods-segmented-control[formControlName],ods-segmented-control[ngModel],ods-segmented-control[odsFormControl]'
            }]
    }], null, null); })();

/**
 * @description
 * A `ControlValueAccessor` for writing a value and listening to changes on an `<ods-select>` element.
 *
 * @usageNotes
 *
 * ### Using an Orchid select dropdown with a reactive form.
 *
 * The following example shows how to use a select dropdown with a reactive form.
 *
 * ```ts
 * const selectControl = new FormControl();
 * ```
 *
 * ```
 * <ods-select [formControl]="selectControl">
 *  <ods-select-option value="option-a">Option A</ods-select-option>
 *  <ods-select-option value="option-b">Option B</ods-select-option>
 *  <ods-select-option value="option-c">Option C</ods-select-option>
 * </ods-select>
 * ```
 *
 * @ngModule ReactiveFormsModule
 * @ngModule FormsModule
 * @publicApi
 */
class OrchidSelectValueAccessorDirective extends OrchidBaseValueAccessor {
    static { this.ɵfac = /*@__PURE__*/ function () { let ɵOrchidSelectValueAccessorDirective_BaseFactory; return function OrchidSelectValueAccessorDirective_Factory(t) { return (ɵOrchidSelectValueAccessorDirective_BaseFactory || (ɵOrchidSelectValueAccessorDirective_BaseFactory = i0.ɵɵgetInheritedFactory(OrchidSelectValueAccessorDirective)))(t || OrchidSelectValueAccessorDirective); }; }(); }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: OrchidSelectValueAccessorDirective, selectors: [["ods-select", "formControl", ""], ["ods-select", "formControlName", ""], ["ods-select", "ngModel", ""], ["ods-select", "odsFormControl", ""]], hostVars: 1, hostBindings: function OrchidSelectValueAccessorDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("ngModelChange", function OrchidSelectValueAccessorDirective_ngModelChange_HostBindingHandler($event) { return ctx.value = $event; });
        } if (rf & 2) {
            i0.ɵɵhostProperty("ngModel", ctx.value);
        } }, features: [i0.ɵɵProvidersFeature([
                {
                    multi: true,
                    provide: NG_VALUE_ACCESSOR,
                    useExisting: forwardRef(() => OrchidSelectValueAccessorDirective)
                }
            ]), i0.ɵɵInheritDefinitionFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrchidSelectValueAccessorDirective, [{
        type: Directive,
        args: [{
                host: {
                    '(ngModelChange)': 'value = $event',
                    '[ngModel]': 'value'
                },
                providers: [
                    {
                        multi: true,
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef(() => OrchidSelectValueAccessorDirective)
                    }
                ],
                selector: 'ods-select[formControl],ods-select[formControlName],ods-select[ngModel],ods-select[odsFormControl]'
            }]
    }], null, null); })();

/**
 * @description
 * A `ControlValueAccessor` for writing a value and listening to changes on an `<ods-switch>` element.
 *
 * @usageNotes
 *
 * ### Using an Orchid switch with a reactive form.
 *
 * The following example shows how to use a switch with a reactive form.
 *
 * ```ts
 * const switchControl = new FormControl();
 * ```
 *
 * ```
 * <ods-switch [formControl]="switchControl" label="Switch" value="switch"></ods-switch>
 * ```
 *
 * @ngModule ReactiveFormsModule
 * @ngModule FormsModule
 * @publicApi
 */
class OrchidSwitchValueAccessorDirective extends CheckboxControlValueAccessor {
    static { this.ɵfac = /*@__PURE__*/ function () { let ɵOrchidSwitchValueAccessorDirective_BaseFactory; return function OrchidSwitchValueAccessorDirective_Factory(t) { return (ɵOrchidSwitchValueAccessorDirective_BaseFactory || (ɵOrchidSwitchValueAccessorDirective_BaseFactory = i0.ɵɵgetInheritedFactory(OrchidSwitchValueAccessorDirective)))(t || OrchidSwitchValueAccessorDirective); }; }(); }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: OrchidSwitchValueAccessorDirective, selectors: [["ods-switch", "formControl", ""], ["ods-switch", "formControlName", ""], ["ods-switch", "ngModel", ""], ["ods-switch", "odsFormControl", ""]], hostBindings: function OrchidSwitchValueAccessorDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("blur", function OrchidSwitchValueAccessorDirective_blur_HostBindingHandler() { return ctx.onTouched(); })("change", function OrchidSwitchValueAccessorDirective_change_HostBindingHandler($event) { return ctx.onChange($event.target.checked); });
        } }, features: [i0.ɵɵProvidersFeature([
                {
                    multi: true,
                    provide: NG_VALUE_ACCESSOR,
                    useExisting: forwardRef(() => OrchidSwitchValueAccessorDirective)
                }
            ]), i0.ɵɵInheritDefinitionFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrchidSwitchValueAccessorDirective, [{
        type: Directive,
        args: [{
                host: { '(blur)': 'onTouched()', '(change)': 'onChange($event.target.checked)' },
                providers: [
                    {
                        multi: true,
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef(() => OrchidSwitchValueAccessorDirective)
                    }
                ],
                selector: 'ods-switch[formControl],ods-switch[formControlName],ods-switch[ngModel],ods-switch[odsFormControl]'
            }]
    }], null, null); })();

/**
 * @description
 * A `ControlValueAccessor` for writing a value and listening to changes on an `<ods-text-area>` element.
 *
 * @usageNotes
 *
 * ### Using an Orchid text area with a reactive form.
 *
 * The following example shows how to use a text area with a reactive form.
 *
 * ```ts
 * const textAreaControl = new FormControl();
 * ```
 *
 * ```
 * <ods-text-area [formControl]="textAreaControl"></ods-text-area>
 * ```
 *
 * @ngModule ReactiveFormsModule
 * @ngModule FormsModule
 * @publicApi
 */
class OrchidTextAreaValueAccessorDirective extends OrchidBaseValueAccessor {
    static { this.ɵfac = /*@__PURE__*/ function () { let ɵOrchidTextAreaValueAccessorDirective_BaseFactory; return function OrchidTextAreaValueAccessorDirective_Factory(t) { return (ɵOrchidTextAreaValueAccessorDirective_BaseFactory || (ɵOrchidTextAreaValueAccessorDirective_BaseFactory = i0.ɵɵgetInheritedFactory(OrchidTextAreaValueAccessorDirective)))(t || OrchidTextAreaValueAccessorDirective); }; }(); }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: OrchidTextAreaValueAccessorDirective, selectors: [["ods-text-area", "formControl", ""], ["ods-text-area", "formControlName", ""], ["ods-text-area", "ngModel", ""], ["ods-text-area", "odsFormControl", ""]], hostVars: 1, hostBindings: function OrchidTextAreaValueAccessorDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("input", function OrchidTextAreaValueAccessorDirective_input_HostBindingHandler($event) { return ctx._onChange($event.target.value); })("ngModelChange", function OrchidTextAreaValueAccessorDirective_ngModelChange_HostBindingHandler($event) { return ctx.value = $event; });
        } if (rf & 2) {
            i0.ɵɵhostProperty("ngModel", ctx.value);
        } }, features: [i0.ɵɵProvidersFeature([
                {
                    multi: true,
                    provide: NG_VALUE_ACCESSOR,
                    useExisting: forwardRef(() => OrchidTextAreaValueAccessorDirective)
                }
            ]), i0.ɵɵInheritDefinitionFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrchidTextAreaValueAccessorDirective, [{
        type: Directive,
        args: [{
                host: {
                    '(input)': '_onChange($event.target.value)',
                    '(ngModelChange)': 'value = $event',
                    '[ngModel]': 'value'
                },
                providers: [
                    {
                        multi: true,
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef(() => OrchidTextAreaValueAccessorDirective)
                    }
                ],
                selector: 'ods-text-area[formControl],ods-text-area[formControlName],ods-text-area[ngModel],ods-text-area[odsFormControl]'
            }]
    }], null, null); })();

/**
 * @description
 * A `ControlValueAccessor` for writing a value and listening to changes on an `<ods-text-input>` element.
 *
 * @usageNotes
 *
 * ### Using an Orchid text input with a reactive form.
 *
 * The following example shows how to use a text input with a reactive form.
 *
 * ```ts
 * const textInputControl = new FormControl();
 * ```
 *
 * ```
 * <ods-text-input [formControl]="textInputControl"></ods-text-input>
 * ```
 *
 * @ngModule ReactiveFormsModule
 * @ngModule FormsModule
 * @publicApi
 */
class OrchidTextInputValueAccessorDirective extends OrchidBaseValueAccessor {
    static { this.ɵfac = /*@__PURE__*/ function () { let ɵOrchidTextInputValueAccessorDirective_BaseFactory; return function OrchidTextInputValueAccessorDirective_Factory(t) { return (ɵOrchidTextInputValueAccessorDirective_BaseFactory || (ɵOrchidTextInputValueAccessorDirective_BaseFactory = i0.ɵɵgetInheritedFactory(OrchidTextInputValueAccessorDirective)))(t || OrchidTextInputValueAccessorDirective); }; }(); }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: OrchidTextInputValueAccessorDirective, selectors: [["ods-text-input", "formControl", ""], ["ods-text-input", "formControlName", ""], ["ods-text-input", "ngModel", ""], ["ods-text-input", "odsFormControl", ""]], hostVars: 1, hostBindings: function OrchidTextInputValueAccessorDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("input", function OrchidTextInputValueAccessorDirective_input_HostBindingHandler($event) { return ctx._onChange($event.target.value); })("ngModelChange", function OrchidTextInputValueAccessorDirective_ngModelChange_HostBindingHandler($event) { return ctx.value = $event; });
        } if (rf & 2) {
            i0.ɵɵhostProperty("ngModel", ctx.value);
        } }, features: [i0.ɵɵProvidersFeature([
                {
                    multi: true,
                    provide: NG_VALUE_ACCESSOR,
                    useExisting: forwardRef(() => OrchidTextInputValueAccessorDirective)
                }
            ]), i0.ɵɵInheritDefinitionFeature] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrchidTextInputValueAccessorDirective, [{
        type: Directive,
        args: [{
                host: {
                    '(input)': '_onChange($event.target.value)',
                    '(ngModelChange)': 'value = $event',
                    '[ngModel]': 'value'
                },
                providers: [
                    {
                        multi: true,
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef(() => OrchidTextInputValueAccessorDirective)
                    }
                ],
                selector: 'ods-text-input[formControl],ods-text-input[formControlName],ods-text-input[ngModel],ods-text-input[odsFormControl]'
            }]
    }], null, null); })();

/** Directives defined in the `OrchidFormsModule`. */
const FORM_DIRECTIVES = [
    OrchidAutosuggestValueAccessorDirective,
    OrchidCardValueAccessorDirective,
    OrchidCheckboxValueAccessorDirective,
    OrchidDatePickerValueAccessorDirective,
    OrchidRadioValueAccessorDirective,
    OrchidSegmentedControlValueAccessorDirective,
    OrchidSelectValueAccessorDirective,
    OrchidSwitchValueAccessorDirective,
    OrchidTextAreaValueAccessorDirective,
    OrchidTextInputValueAccessorDirective
];
/**
 * @description
 *
 * Adds directives to bind `@angular/forms` value accessors to Orchid form elements.
 *
 * Orchid form elements will function without these directives, even in an Angular application _with_ reactive
 * forms, but each form element value will need to be bound to `@angular/forms` manually.
 *
 * @publicApi
 */
class OrchidFormsModule {
    static { this.ɵfac = function OrchidFormsModule_Factory(t) { return new (t || OrchidFormsModule)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: OrchidFormsModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({}); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrchidFormsModule, [{
        type: NgModule,
        args: [{
                declarations: FORM_DIRECTIVES,
                exports: FORM_DIRECTIVES
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(OrchidFormsModule, { declarations: [OrchidAutosuggestValueAccessorDirective,
        OrchidCardValueAccessorDirective,
        OrchidCheckboxValueAccessorDirective,
        OrchidDatePickerValueAccessorDirective,
        OrchidRadioValueAccessorDirective,
        OrchidSegmentedControlValueAccessorDirective,
        OrchidSelectValueAccessorDirective,
        OrchidSwitchValueAccessorDirective,
        OrchidTextAreaValueAccessorDirective,
        OrchidTextInputValueAccessorDirective], exports: [OrchidAutosuggestValueAccessorDirective,
        OrchidCardValueAccessorDirective,
        OrchidCheckboxValueAccessorDirective,
        OrchidDatePickerValueAccessorDirective,
        OrchidRadioValueAccessorDirective,
        OrchidSegmentedControlValueAccessorDirective,
        OrchidSelectValueAccessorDirective,
        OrchidSwitchValueAccessorDirective,
        OrchidTextAreaValueAccessorDirective,
        OrchidTextInputValueAccessorDirective] }); })();

/**
 * When the `[routerLink]` directive is used with an element that is not an `<a>` tag, routing works as expected, but keyboard
 * functionality does not. `[routerLink]` specifically checks if the element is an `<a>` tag.
 * @see {@link https://github.com/angular/angular/blob/710a24a7d058143e94cbc2db905523bfeb257397/packages/router/src/directives/router_link.ts#L190}
 *
 * In our case, our `<ods-link>` element is a custom element that contains an `<a>` tag, but `[routerLink]` only checks the
 * host element tag. This workaround enables basic keyboard functionality when `[routerLink]` is used with `<ods-link>`.
 * @see {@link https://stackoverflow.com/a/76563632}
 *
 * @ngModule OrchidRouterModule
 *
 * @publicApi
 */
class OrchidRouterLinkDirective {
    static { this.ɵfac = function OrchidRouterLinkDirective_Factory(t) { return new (t || OrchidRouterLinkDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: OrchidRouterLinkDirective, selectors: [["ods-link", "routerLink", ""]], hostBindings: function OrchidRouterLinkDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("keydown.enter", function OrchidRouterLinkDirective_keydown_enter_HostBindingHandler($event) { return $event.target.click(); });
        } } }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrchidRouterLinkDirective, [{
        type: Directive,
        args: [{
                host: { '(keydown.enter)': '$any($event.target).click()' },
                selector: 'ods-link[routerLink]'
            }]
    }], null, null); })();

/** Directives defined in the `OrchidRouterModule`. */
const ROUTER_DIRECTIVES = [OrchidRouterLinkDirective];
/**
 * @description
 *
 * Adds directives to enhance user accessibility when using `@angular/router` with `<ods-link>`.
 *
 * `<ods-link>` will function without these directives, even in an Angular application _with_ routing, but
 * full user accessibility won't be guaranteed.
 *
 * @publicApi
 */
class OrchidRouterModule {
    static { this.ɵfac = function OrchidRouterModule_Factory(t) { return new (t || OrchidRouterModule)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: OrchidRouterModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({}); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrchidRouterModule, [{
        type: NgModule,
        args: [{
                declarations: ROUTER_DIRECTIVES,
                exports: ROUTER_DIRECTIVES
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(OrchidRouterModule, { declarations: [OrchidRouterLinkDirective], exports: [OrchidRouterLinkDirective] }); })();

/**
 * @description
 *
 * Adds directives that enhance Orchid elements with Angular functionality.
 *
 * Orchid elements will function without these directives, as long as `CUSTOM_ELEMENTS_SCHEMA` is defined, but
 * usage of this module is recommended for ease of use.
 *
 * @publicApi
 */
class OrchidModule {
    static { this.ɵfac = function OrchidModule_Factory(t) { return new (t || OrchidModule)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: OrchidModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule, OrchidFormsModule, OrchidRouterModule] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrchidModule, [{
        type: NgModule,
        args: [{
                exports: [OrchidFormsModule, OrchidRouterModule],
                imports: [CommonModule]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(OrchidModule, { imports: [CommonModule], exports: [OrchidFormsModule, OrchidRouterModule] }); })();

/**
 * @module
 * @description
 * Entry point for all public APIs of this package.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { OrchidAutosuggestValueAccessorDirective, OrchidCardValueAccessorDirective, OrchidCheckboxValueAccessorDirective, OrchidDatePickerValueAccessorDirective, OrchidFormsModule, OrchidModule, OrchidRadioValueAccessorDirective, OrchidRouterLinkDirective, OrchidRouterModule, OrchidSegmentedControlValueAccessorDirective, OrchidSelectValueAccessorDirective, OrchidSwitchValueAccessorDirective, OrchidTextAreaValueAccessorDirective, OrchidTextInputValueAccessorDirective };
